import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  compositeScore,
  candidateDisagreementScore,
  DISAGREEMENT_THRESHOLD,
  type Candidate,
  type ScoreMap,
} from "./mockSelectionData";
import { SelectionReportModal } from "./SelectionReportModal";
import { SelectionComparisonModal } from "./SelectionComparisonModal";
import type { Bucket } from "@/pages/hub/HubSelectionDemo";

interface Props {
  candidates: Candidate[];
  scores: ScoreMap;
  buckets: Record<Bucket, number[]>;
  onMove: (candidateId: number, target: Bucket) => void;
}

const BUCKET_META: Record<Bucket, { label: string; color: string; headerBg: string; border: string }> = {
  selected:     { label: "Selected",     color: "text-green-700",  headerBg: "bg-green-50",  border: "border-green-200" },
  reserves:     { label: "Reserves",     color: "text-blue-700",   headerBg: "bg-blue-50",   border: "border-blue-200"  },
  not_selected: { label: "Not Selected", color: "text-gray-600",   headerBg: "bg-gray-50",   border: "border-gray-200"  },
};

const BUCKET_ORDER: Bucket[] = ["selected", "reserves", "not_selected"];
const OTHER_BUCKETS: Record<Bucket, Bucket[]> = {
  selected:     ["reserves", "not_selected"],
  reserves:     ["selected", "not_selected"],
  not_selected: ["selected", "reserves"],
};

export function SelectionDecisionTab({ candidates, scores, buckets, onMove }: Props) {
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [moveTarget, setMoveTarget] = useState<Record<number, Bucket>>({});
  const [reportCandidate, setReportCandidate] = useState<Candidate | null>(null);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  function toggleCompare(id: number) {
    setSelectedForCompare((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleNote(id: number) {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function currentBucket(candidateId: number): Bucket {
    return (Object.keys(buckets) as Bucket[]).find((b) =>
      buckets[b].includes(candidateId)
    ) ?? "not_selected";
  }

  function exportSquadCSV() {
    const rows: string[] = [
      ["Name", "Position", "Age Group", "Composite Score", "Status", "Notes"].join(",")
    ];
    for (const bucket of BUCKET_ORDER) {
      for (const candidateId of buckets[bucket]) {
        const c = candidates.find(x => x.id === candidateId);
        if (!c) continue;
        const score = compositeScore(candidateId).toFixed(2);
        const note = (notes[candidateId] ?? "").replace(/"/g, '""');
        rows.push([
          `"${c.name}"`,
          `"${c.position}"`,
          `"${c.ageGroup ?? ""}"`,
          score,
          `"${BUCKET_META[bucket].label}"`,
          `"${note}"`
        ].join(","));
      }
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hub-selection-squad-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleMove(candidateId: number) {
    const cur = currentBucket(candidateId);
    const target = moveTarget[candidateId] ?? OTHER_BUCKETS[cur][0];
    onMove(candidateId, target);
    setMoveTarget((prev) => { const n = {...prev}; delete n[candidateId]; return n; });
  }

  const total = candidates.length;
  const selectedCount = buckets.selected.length;
  const reservesCount = buckets.reserves.length;
  const notSelectedCount = buckets.not_selected.length;

  return (
    <div className="space-y-5" data-testid="selection-decision-tab">
      {/* Instruction caption */}
      <p className="text-sm text-gray-500 text-center">
        Move candidates between columns to build your squad. Each candidate starts in{" "}
        <span className="font-medium text-gray-700">Not Selected</span> until you sort them.
      </p>

      {/* Sticky compare bar */}
      {selectedForCompare.length >= 2 && (
        <div
          className="sticky top-0 z-20 flex items-center justify-between gap-3 bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow"
          data-testid="compare-bar"
        >
          <span className="text-sm font-medium">
            {selectedForCompare.length} candidates selected
          </span>
          <div className="flex items-center gap-2">
            <button
              className="text-xs text-blue-200 hover:text-white underline"
              onClick={() => setSelectedForCompare([])}
              data-testid="btn-clear-compare"
            >
              Clear
            </button>
            <Button
              size="sm"
              className="bg-white text-blue-700 hover:bg-blue-50 h-7 px-3 text-xs"
              onClick={() => setCompareOpen(true)}
              data-testid="btn-open-compare"
            >
              Compare ({selectedForCompare.length})
            </Button>
          </div>
        </div>
      )}

      {/* 3-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {BUCKET_ORDER.map((bucket) => {
          const meta = BUCKET_META[bucket];
          const bucketCandidates = candidates.filter((c) =>
            buckets[bucket].includes(c.id)
          );

          return (
            <div
              key={bucket}
              className={`rounded-xl border ${meta.border} overflow-hidden`}
              data-testid={`bucket-column-${bucket}`}
            >
              {/* Column header */}
              <div className={`${meta.headerBg} px-4 py-3 flex items-center justify-between`}>
                <span className={`font-semibold text-sm ${meta.color}`}>{meta.label}</span>
                <span
                  className={`text-xs font-bold rounded-full px-2 py-0.5 border ${meta.border} ${meta.color} bg-white`}
                  data-testid={`bucket-count-${bucket}`}
                >
                  {bucketCandidates.length}
                </span>
              </div>

              {/* Candidate cards */}
              <div className="divide-y divide-gray-100 min-h-[120px] bg-white">
                {bucketCandidates.length === 0 && (
                  <p className="text-xs text-gray-300 text-center py-8 italic">Empty</p>
                )}
                {bucketCandidates.map((candidate) => {
                  const composite = compositeScore(candidate.id).toFixed(2);
                  const hasDisagreement =
                    candidateDisagreementScore(candidate.id) > DISAGREEMENT_THRESHOLD;
                  const noteOpen = expandedNotes.has(candidate.id);
                  const cur = currentBucket(candidate.id);
                  const otherBuckets = OTHER_BUCKETS[cur];
                  const selectedTarget = moveTarget[candidate.id] ?? otherBuckets[0];

                  return (
                    <div
                      key={candidate.id}
                      className="px-3 py-3 space-y-2"
                      data-testid={`candidate-card-${candidate.id}`}
                    >
                      {/* Name + score row */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <input
                            type="checkbox"
                            className="mt-0.5 shrink-0 accent-blue-600 cursor-pointer"
                            checked={selectedForCompare.includes(candidate.id)}
                            disabled={!selectedForCompare.includes(candidate.id) && selectedForCompare.length >= 4}
                            onChange={() => toggleCompare(candidate.id)}
                            aria-label={`Select ${candidate.name} for comparison`}
                            data-testid={`compare-checkbox-${candidate.id}`}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {candidate.name}
                            </p>
                            <p className="text-xs text-gray-400 truncate">{candidate.position}</p>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-white bg-gray-700 rounded-full px-2 py-0.5 shrink-0">
                          {composite}
                        </span>
                      </div>

                      {/* Disagreement badge */}
                      {hasDisagreement && (
                        <Badge className="bg-amber-100 text-amber-700 border border-amber-300 text-xs font-normal">
                          ⚠ disagreement
                        </Badge>
                      )}

                      {/* Move control */}
                      <div className="flex items-center gap-1.5">
                        <Select
                          value={selectedTarget}
                          onValueChange={(v) =>
                            setMoveTarget((prev) => ({ ...prev, [candidate.id]: v as Bucket }))
                          }
                        >
                          <SelectTrigger
                            className="h-7 text-xs flex-1"
                            data-testid={`move-select-${candidate.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {otherBuckets.map((b) => (
                              <SelectItem key={b} value={b} className="text-xs">
                                {BUCKET_META[b].label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs shrink-0"
                          onClick={() => handleMove(candidate.id)}
                          data-testid={`move-btn-${candidate.id}`}
                        >
                          Move →
                        </Button>
                      </div>

                      {/* View Report */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs w-full text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        onClick={() => setReportCandidate(candidate)}
                        data-testid={`view-report-btn-${candidate.id}`}
                      >
                        View Report
                      </Button>

                      {/* Note toggle + textarea */}
                      <button
                        onClick={() => toggleNote(candidate.id)}
                        className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
                        data-testid={`note-toggle-${candidate.id}`}
                      >
                        {noteOpen ? "hide note" : "+ add note"}
                      </button>
                      {noteOpen && (
                        <Textarea
                          className="text-xs resize-none h-14"
                          placeholder="Rationale for this decision…"
                          value={notes[candidate.id] ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setNotes((prev) => ({ ...prev, [candidate.id]: e.target.value }))
                          }
                          data-testid={`note-input-${candidate.id}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison Modal */}
      {compareOpen && (
        <SelectionComparisonModal
          candidates={candidates.filter((c) => selectedForCompare.includes(c.id))}
          onClose={() => setCompareOpen(false)}
        />
      )}

      {/* Selection Report Modal */}
      <SelectionReportModal
        candidate={reportCandidate}
        bucket={reportCandidate ? currentBucket(reportCandidate.id) : "not_selected"}
        rank={
          reportCandidate
            ? [...candidates]
                .sort((a, b) => compositeScore(b.id) - compositeScore(a.id))
                .findIndex((c) => c.id === reportCandidate.id) + 1
            : 0
        }
        onClose={() => setReportCandidate(null)}
      />

      {/* Footer */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-sm text-gray-500" data-testid="squad-counter">
          Selected: <span className="font-semibold text-gray-800">{selectedCount}</span>
          {" · "}Reserves: <span className="font-semibold text-gray-800">{reservesCount}</span>
          {" · "}Not Selected: <span className="font-semibold text-gray-800">{notSelectedCount}</span>
          {" · "}Total: {total}
        </p>
        <Button
          className="bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
          onClick={exportSquadCSV}
          data-testid="btn-export-squad"
        >
          Export Squad
        </Button>
      </div>
    </div>
  );
}
